import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { toast } from 'sonner';

interface CartItem {
  id: number;
  name: string;
  price: number;
  image: string;
  size?: string;
  color?: string;
  quantity: number;
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: Omit<CartItem, 'quantity'>, maxStock?: number) => void;
  removeFromCart: (id: number) => void;
  updateQuantity: (id: number, quantity: number, maxStock?: number) => void;
  clearCart: () => void;
  cartCount: number;
  cartTotal: number;
}

const CartContext = createContext<CartContextType | null>(null);

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem('cartItems');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  // Sepeti localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (item: Omit<CartItem, 'quantity'>, maxStock?: number) => {
    setCartItems(prev => {
      const existing = prev.find(i => i.id === item.id && i.size === item.size && i.color === item.color);
      if (existing) {
        if (maxStock !== undefined && existing.quantity >= maxStock) {
          toast.error(`Bu üründen en fazla ${maxStock} adet ekleyebilirsiniz`);
          return prev;
        }
        toast.success('Ürün adedi artırıldı');
        return prev.map(i => (i.id === item.id && i.size === item.size && i.color === item.color) ? { ...i, quantity: i.quantity + 1 } : i);
      }
      if (maxStock !== undefined && maxStock <= 0) {
        toast.error('Bu ürün stokta yok');
        return prev;
      }
      toast.success('Ürün sepete eklendi!');
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const removeFromCart = (id: number) => {
    setCartItems(prev => prev.filter(i => i.id !== id));
    toast.success('Ürün sepetten çıkarıldı');
  };

  const updateQuantity = (id: number, quantity: number, maxStock?: number) => {
    if (quantity < 1) return;
    if (maxStock !== undefined && quantity > maxStock) {
      toast.error(`Bu üründen en fazla ${maxStock} adet ekleyebilirsiniz`);
      return;
    }
    setCartItems(prev => prev.map(i => i.id === id ? { ...i, quantity } : i));
  };

  const clearCart = () => setCartItems([]);

  const cartCount = cartItems.reduce((sum, i) => sum + i.quantity, 0);
  const cartTotal = cartItems.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{ cartItems, addToCart, removeFromCart, updateQuantity, clearCart, cartCount, cartTotal }}>
      {children}
    </CartContext.Provider>
  );
}
